import { useQuery } from '@tanstack/react-query';
import { get } from '../../utils/networking';

enum CacheKey {
  GetInterpretiveReport = 'GetInterpretiveReport',
}

/**
 * Types for Interpretive Report API
 *
 * IMPORTANT: These types must match the backend DTOs exactly.
 * Backend source: packages/trustpulse-api/src/application/use-cases/interpretive-report/generate-interpretive-report/interpretive-report.dto.ts
 */
export interface DataQualityWarning {
  type: 'UNIFORM_EXTREME_LOW' | 'UNIFORM_EXTREME_HIGH';
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
}

export interface InterpretiveReportDto {
  surveyId: number;
  teamId: number;
  tenantId: number;
  language: 'NB' | 'EN';
  createdAt: string;
  archetype: {
    name: string;
    description: string;
    profileNarrative: string;
    keyStrengths: string[];
    keyChallenges: string[];
    primaryTension: string | null;
  };
  developmentStage: {
    previousStage: string | null;
    /** The name of the stage in the language of the report */
    currentStageName: string;
    currentStageDescription: string;
    nextStageName: string | null;
    nextStageDescription: string;
    pitfalls: string[];
  };
  dimensions: Array<{
    dimensionName: string;
    displayName: string;
    rank: number;
    score: number;
    maxScore: number;
    percentageScore: number;
    standardDeviation: number;
    agreementLevel: string;
    agreementDescription: string;
    responseDistribution: { 1: number; 2: number; 3: number; 4: number; 5: number };
    whatItMeans: string;
    everydayImpact: string;
    reflectionQuestion: string;
  }>;
  varianceAnalysis: string | null;
  conversationPrompts: Array<{
    sequenceNumber: number;
    question: string;
    purpose: string;
  }>;
  dataQualityWarning: DataQualityWarning | null;
  summary: {
    overallScore: number;
    averageStandardDeviation: number;
    /** How much the team agrees overall, with the same limits as each dimension */
    agreementLevel: 'HIGH' | 'MODERATE' | 'LOW';
    strongestDimension: string;
    weakestDimension: string;
    hasSignificantVariance: boolean;
  };
  methodology: {
    respondents: number;
    expectedRespondents: number;
    responseRate: number;
    confidenceLevel: 'high' | 'medium' | 'low';
    measurementNumber: number;
    totalMeasurements: number;
    isFirstMeasurement: boolean;
    hasLowResponseRate: boolean;
  };
}

/**
 * Custom hook for fetching interpretive report
 *
 * This hook fetches the interpretive report for a survey.
 * It should be used in conjunction with useGetInterpretiveReportStatusQuery
 * to poll for generation progress.
 *
 * @param surveyId - The survey ID
 * @param enabled - Whether the query should run (default: true if surveyId exists)
 * @returns TanStack Query result with report data
 *
 * @example
 * ```tsx
 * const { data: report, isLoading, error, refetch } = useGetInterpretiveReportQuery(surveyId);
 * const { data: status } = useGetInterpretiveReportStatusQuery(surveyId);
 *
 * // When status becomes COMPLETED, refetch the report
 * useEffect(() => {
 *   if (status?.status === 'COMPLETED') {
 *     refetch();
 *   }
 * }, [status?.status, refetch]);
 * ```
 */
export const useGetInterpretiveReportQuery = (
  surveyId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery<InterpretiveReportDto>({
    queryKey: [CacheKey.GetInterpretiveReport, surveyId],
    queryFn: () => get<InterpretiveReportDto>(`/api/interpretive-report/survey/${surveyId}`),
    enabled: !!surveyId && enabled,
    // Don't refetch automatically - we'll control this based on generation status
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};
